import React, {
  useMemo,
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Flex,
  Button,
  Paragraph,
  LoadingContainer
} from '@sopranodesign/pulse-components';
import {
  useQuery,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import { usePaginationReducer } from '@sopranodesign/pulse-components';

import {
  fetchCategories,
  getZendeskSettings
} from '../api';
import ZendeskCategoriesList from './ZendeskCategoriesList';
import ZendeskArticlesList from './ZendeskArticlesList';
import ZendeskModalManager from './ZendeskModalManager';

const ZendeskKnowledgeBase = ({ settings }) => {
  const queryClient = useQueryClient();

  const pagination = usePaginationReducer({
    pathname: '/zendesk',
    fetchQueryKey: ['generative-ai/zendesk']
  });

  const [activeModal, setActiveModal] = useState(null);
  const [modalProps, setModalProps] = useState({});

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [isViewingArticles, setIsViewingArticles] = useState(false);

  const [categories, setCategories] = useState([]);
  const [isAddCategoryLoading, setIsAddCategoryLoading] = useState(false);

  const [categoryLoadingIds, setCategoryLoadingIds] = useState({});
  const [articleLoadingIds, setArticleLoadingIds] = useState({});
  const [errorIds, setErrorIds] = useState({});
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const openModal = (type, props = {}) => {
    setActiveModal(type);
    setModalProps(props);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalProps({});
  };

  const { data, isLoading } = useQuery({
    queryKey: ['generative-ai/zendesk/', pagination.params],
    queryFn: () => getZendeskSettings(pagination.params),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const responseData = useMemo(
    () => data?.data ?? {},
    [data]
  );

  const paginatedCategories = responseData.categories ?? [];
  const meta = responseData.meta ?? {};
  const hasPending = responseData.has_pending ?? false;

  useEffect(() => {
    setShouldRefetch(!isViewingArticles && hasPending);
  }, [
    hasPending,
    isViewingArticles
  ]);

  useEffect(() => {
    if (!isViewingArticles) {
      setArticleLoadingIds({});
    }
  }, [isViewingArticles]);

  if (isLoading) {
    return (
      <Flex col>
        <Flex col center middle tall>
          <LoadingContainer />
          <Paragraph mt>Loading Knowledge Base...</Paragraph>
        </Flex>
      </Flex>
    );
  }

  return (
    <>
      {paginatedCategories?.length === 0 && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>
              Choose Zendesk Categories to Sync.
            </Paragraph>

            <Button
              icon="plus"
              variant="secondary"
              loading={isAddCategoryLoading}
              onClick={async () => {
                setIsAddCategoryLoading(true);

                try {
                  const response = await fetchCategories();

                  setCategories(
                    response.data ?? []
                  );

                  openModal('category');
                } finally {
                  setIsAddCategoryLoading(false);
                }
              }}
            >
              Select Categories
            </Button>
          </Flex>
        </Box>
      )}

      {paginatedCategories?.length > 0 && (
        <ZendeskCategoriesList
          categories={paginatedCategories}
          settings={settings}
          loadingIds={categoryLoadingIds}
          isSyncing={isSyncingAll}
          hasPending={hasPending}
          pagination={{
            meta,
            props: pagination.props
          }}
          onView={(id) => {
            setSelectedCategoryId(id);
            setIsViewingArticles(true);
          }}
          onResync={(category) =>
            openModal('confirmation', {
              type: 'resync',
              category
            })
          }
          onDelete={(category) =>
            openModal('confirmation', {
              type: 'delete',
              category
            })
          }
          onAddCategory={async () => {
            const response =
              await fetchCategories();

            setCategories(
              response.data ?? []
            );

            openModal('category');
          }}
          onResyncAll={() =>
            openModal('confirmation', {
              type: 'resyncAll'
            })
          }
          isAddCategoryLoading={
            isAddCategoryLoading
          }
        />
      )}

      {selectedCategoryId && (
        <ZendeskArticlesList
          categoryId={selectedCategoryId}
          handleHide={() => {
            setSelectedCategoryId(null);
            setIsViewingArticles(false);

            queryClient.invalidateQueries({
              queryKey: [
                'generative-ai/zendesk/'
              ]
            });
          }}
          onOpenTagsModal={(article) =>
            openModal('tags', {
              article,
              categoryId:
                selectedCategoryId
            })
          }
          onOpenRecrawlModal={(article) =>
            openModal('confirmation', {
              type: 'recrawl',
              article,
              categoryId:
                selectedCategoryId
            })
          }
          loadingIds={articleLoadingIds}
          errorIds={errorIds}
        />
      )}

      <ZendeskModalManager
        activeModal={activeModal}
        modalProps={modalProps}
        closeModal={closeModal}
        categories={categories}
        setErrorIds={setErrorIds}
        setCategoryLoadingIds={
          setCategoryLoadingIds
        }
        setArticleLoadingIds={
          setArticleLoadingIds
        }
        setIsSyncingAll={setIsSyncingAll}
      />
    </>
  );
};

ZendeskKnowledgeBase.propTypes = {
  settings: PropTypes.shape({
    status: PropTypes.number,
    uses_oauth: PropTypes.bool,
    updated_at: PropTypes.string
  })
};

export default ZendeskKnowledgeBase;